import type { ColumnDef } from "@tanstack/react-table"
import type {
  ColumnFilterConfig,
  ColumnFilterType,
  FilterCombinator,
  FilterCondition,
  FilterGroup,
  FilterOperator,
} from "../table.types"

/**
 * A filterable column as surfaced to the advanced-filter builder.
 */
export type AdvancedFilterColumn = {
  id: string
  label: string
  filter: ColumnFilterConfig
}

/** Human-readable label for each operator, used in the builder dropdown. */
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  EQ: "is",
  NEQ: "is not",
  CONTAINS: "contains",
  GTE: "is greater than or equal / \u2265",
  LTE: "is less than or equal / \u2264",
  GT: "is greater than / >",
  LT: "is less than / <",
  IN: "is any of",
  IS_NULL: "is empty",
}

/**
 * Returns true when a filter type represents dates (where before/after reads
 * more naturally than greater/less).
 */
function isDateFilterType(type: ColumnFilterType): boolean {
  return type === "date" || type === "date-range" || type === "date-filter"
}

/**
 * Type-aware operator label for the builder dropdown. Comparison operators read
 * as "greater/less than" for numbers (and as a sensible default) but switch to
 * "after/before" for date columns, where that wording is intuitive. All other
 * operators fall back to {@link OPERATOR_LABELS}.
 */
export function operatorLabel(
  operator: FilterOperator,
  filterType: ColumnFilterType,
): string {
  if (isDateFilterType(filterType)) {
    switch (operator) {
      case "GT":
        return "is after / >"
      case "GTE":
        return "is on or after / \u2265"
      case "LT":
        return "is before / <"
      case "LTE":
        return "is on or before / \u2264"
    }
  }
  return OPERATOR_LABELS[operator]
}

/** Short label used in the active-filter summary chip. */
const OPERATOR_SUMMARY_LABELS: Record<FilterOperator, string> = {
  EQ: "=",
  NEQ: "\u2260",
  CONTAINS: "contains",
  GTE: "\u2265",
  LTE: "\u2264",
  GT: ">",
  LT: "<",
  IN: "in",
  IS_NULL: "is empty",
}

/**
 * The operators offered for each column filter type. Order matters: the first
 * entry is the default when a column is picked.
 */
export function operatorsForFilterType(
  type: ColumnFilterType,
): FilterOperator[] {
  switch (type) {
    case "text":
      return ["CONTAINS", "EQ", "NEQ", "IS_NULL"]
    case "number":
    case "number-range":
      return ["EQ", "NEQ", "GTE", "LTE", "GT", "LT"]
    case "select":
      return ["EQ", "NEQ", "IN", "IS_NULL"]
    case "boolean":
      return ["EQ"]
    case "date":
    case "date-range":
    case "date-filter":
      return ["EQ", "GTE", "LTE", "GT", "LT"]
    default:
      return ["EQ", "NEQ", "CONTAINS", "IS_NULL"]
  }
}

/**
 * The operators the builder offers for a column: its explicit `operators`
 * allow-list (mirroring the server registry) when set, else the
 * type-derived default.
 */
export function operatorsForColumn(
  column: AdvancedFilterColumn,
): FilterOperator[] {
  return column.filter.operators ?? operatorsForFilterType(column.filter.type)
}

/** Operators that take no value at all. */
export function isUnaryOperator(operator: FilterOperator): boolean {
  return operator === "IS_NULL"
}

/** Operators that take a list of values. */
export function isMultiValueOperator(operator: FilterOperator): boolean {
  return operator === "IN"
}

export function newCondition(columns: AdvancedFilterColumn[]): FilterCondition {
  const first = columns[0]
  const operator = first ? operatorsForColumn(first)[0] : "EQ"
  return {
    column: first?.id ?? "",
    operator,
    value: null,
    values: null,
    negate: false,
  }
}

export function newGroup(combinator: FilterCombinator): FilterGroup {
  return {
    combinator,
    negate: false,
    conditions: [],
    groups: [],
  }
}

/** A fresh root group seeded with one empty condition. */
export function newRootGroup(columns: AdvancedFilterColumn[]): FilterGroup {
  return {
    combinator: "AND",
    negate: false,
    conditions: [newCondition(columns)],
    groups: [],
  }
}

/**
 * Strips empty/incomplete branches so we never emit a condition the server
 * would choke on. Returns null when nothing meaningful remains.
 */
export function pruneGroup(group: FilterGroup): FilterGroup | null {
  const conditions = (group.conditions ?? []).filter(isConditionComplete)
  const groups = (group.groups ?? [])
    .map(pruneGroup)
    .filter((g): g is FilterGroup => g !== null)

  if (conditions.length === 0 && groups.length === 0) return null

  return {
    combinator: group.combinator,
    negate: group.negate ?? false,
    conditions,
    groups,
  }
}

function isConditionComplete(condition: FilterCondition): boolean {
  if (!condition.column) return false
  if (isUnaryOperator(condition.operator)) return true
  if (isMultiValueOperator(condition.operator)) {
    return !!condition.values && condition.values.length > 0
  }
  return (
    condition.value !== null &&
    condition.value !== undefined &&
    condition.value !== ""
  )
}

/** Whether a tree has at least one complete condition. */
export function hasActiveFilter(group: FilterGroup | null): boolean {
  if (!group) return false
  return pruneGroup(group) !== null
}

/**
 * Builds a compact, human-readable summary of a filter tree, e.g.
 * "(Activity = weekly OR Activity = daily) AND Customers in [Placepoint, Bonum]".
 */
export function summarizeGroup(
  group: FilterGroup,
  columnLabel: (columnId: string) => string,
): string {
  const parts: string[] = []

  for (const cond of group.conditions ?? []) {
    if (!isConditionComplete(cond)) continue
    parts.push(summarizeCondition(cond, columnLabel))
  }
  for (const sub of group.groups ?? []) {
    const pruned = pruneGroup(sub)
    if (!pruned) continue
    parts.push(`(${summarizeGroup(pruned, columnLabel)})`)
  }

  const joined = parts.join(group.combinator === "OR" ? " OR " : " AND ")
  return group.negate ? `NOT (${joined})` : joined
}

/**
 * Builds a `(row, columnId) => value` accessor from the table's column defs,
 * mirroring how TanStack reads a cell value (accessorFn, then accessorKey,
 * then the column id as a plain key). Lets the advanced-filter tree be
 * evaluated client-side without a live table instance.
 */
export function buildRowValueGetter<TData>(
  columns: ColumnDef<TData, unknown>[],
): (row: TData, columnId: string) => unknown {
  const byId = new Map<string, (row: TData) => unknown>()
  for (const col of columns) {
    const id = col.id
    if (!id) continue
    if ("accessorFn" in col && typeof col.accessorFn === "function") {
      const fn = col.accessorFn as (row: TData, index: number) => unknown
      byId.set(id, (row) => fn(row, 0))
    } else if ("accessorKey" in col && col.accessorKey != null) {
      const key = String(col.accessorKey)
      byId.set(id, (row) => (row as Record<string, unknown>)[key])
    }
  }
  return (row, columnId) => {
    const fn = byId.get(columnId)
    return fn ? fn(row) : (row as Record<string, unknown>)[columnId]
  }
}

function compareValues(raw: unknown, target: unknown): number {
  const a = Number(raw)
  const b = Number(target)
  if (!Number.isNaN(a) && !Number.isNaN(b)) return a < b ? -1 : a > b ? 1 : 0
  return String(raw ?? "").localeCompare(String(target ?? ""))
}

function matchCondition(
  row: unknown,
  cond: FilterCondition,
  getValue: (row: unknown, columnId: string) => unknown,
): boolean {
  const raw = getValue(row, cond.column)
  const rawStr = String(raw ?? "").toLowerCase()
  let result: boolean

  switch (cond.operator) {
    case "IS_NULL":
      result = raw === null || raw === undefined || raw === ""
      break
    case "IN": {
      const set = new Set(
        (cond.values ?? []).map((v) => String(v).toLowerCase()),
      )
      result = set.has(rawStr)
      break
    }
    case "CONTAINS":
      result = rawStr.includes(String(cond.value ?? "").toLowerCase())
      break
    case "EQ":
      result = rawStr === String(cond.value ?? "").toLowerCase()
      break
    case "NEQ":
      result = rawStr !== String(cond.value ?? "").toLowerCase()
      break
    case "GT":
      result = compareValues(raw, cond.value) > 0
      break
    case "GTE":
      result = compareValues(raw, cond.value) >= 0
      break
    case "LT":
      result = compareValues(raw, cond.value) < 0
      break
    case "LTE":
      result = compareValues(raw, cond.value) <= 0
      break
    default:
      result = true
  }

  return cond.negate ? !result : result
}

function matchGroup(
  row: unknown,
  group: FilterGroup,
  getValue: (row: unknown, columnId: string) => unknown,
): boolean {
  const results: boolean[] = []
  for (const cond of group.conditions ?? []) {
    results.push(matchCondition(row, cond, getValue))
  }
  for (const sub of group.groups ?? []) {
    results.push(matchGroup(row, sub, getValue))
  }

  let result: boolean
  if (results.length === 0) {
    result = true
  } else if (group.combinator === "OR") {
    result = results.some(Boolean)
  } else {
    result = results.every(Boolean)
  }

  return group.negate ? !result : result
}

/**
 * Evaluates a (pruned) advanced-filter tree against an in-memory row set —
 * the client-side counterpart to the server's `filterTree`. Pass a pruned
 * group (see {@link pruneGroup}); incomplete conditions are not handled here.
 */
export function applyAdvancedFilter<TData>(
  rows: TData[],
  group: FilterGroup,
  getValue: (row: TData, columnId: string) => unknown,
): TData[] {
  return rows.filter((row) =>
    matchGroup(row, group, getValue as (r: unknown, c: string) => unknown),
  )
}

function summarizeCondition(
  cond: FilterCondition,
  columnLabel: (columnId: string) => string,
): string {
  const label = columnLabel(cond.column)
  const op = OPERATOR_SUMMARY_LABELS[cond.operator]
  let rendered: string
  if (isUnaryOperator(cond.operator)) {
    rendered = `${label} ${op}`
  } else if (isMultiValueOperator(cond.operator)) {
    rendered = `${label} ${op} [${(cond.values ?? []).join(", ")}]`
  } else {
    rendered = `${label} ${op} ${cond.value ?? ""}`
  }
  return cond.negate ? `NOT (${rendered})` : rendered
}
